import React, { useState, useEffect } from 'react';
import { useClient } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Play, Pause, Square,
  BarChart3, Users, Phone, MessageSquare, Bot, HeadphonesIcon,
  TrendingUp, Target, Clock, Calendar, AlertCircle, CheckCircle,
  Activity, Zap, DollarSign, Star, MapPin, Building, ChevronRight,
  Loader2, Settings as SettingsIcon, X
} from 'lucide-react';
import CampaignDetailsModal from './CampaignDetailsModal';
import FloatingParticles from './FloatingParticles';
import EnhancedCard from './EnhancedCard';
import EnhancedButton from './EnhancedButton';
import PageHeader from './ui/PageHeader';
import '../styles/neutral-theme.css';
import '../styles/campaigns-modern.css';

const CampaignManagement = () => {
  const { t, currentLanguage, renderKey } = useLanguage();
  const client = useClient();
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaignForDetails, setSelectedCampaignForDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaignForConfig, setSelectedCampaignForConfig] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/campaigns`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Adapt data to UI
        const formattedData = data.map(c => ({
          id: c.id,
          name: c.name,
          status: c.status || 'active',
          type: c.type || 'sales',
          industry: c.industry || client?.slug || 'general',
          progress: 0, // Backend may not have, set to 0 or fetch from elsewhere
          contacts: 0,
          completed: 0,
          success_rate: 0,
          revenue: 0,
          channel: 'voicebot',
          created: new Date(c.start_date).toISOString().split('T')[0],
          agent: 'Default Agent',
          looker_dashboard_url: c.looker_dashboard_url,
          description: c.description,
          kpis: {
            contact_rate: 80,
            conversion_rate: 50,
            avg_duration: '3m 30s',
            cost_per_contact: 2.0
          }
        }));
        setCampaigns(formattedData);
      } else {
        setError('Error al cargar campañas');
      }
    } catch (err) {
      setError('Error de red');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Campaign types and industries
  const campaignTypes = [
    { id: 'sales', name: t('campaignMgmt.types.sales'), icon: DollarSign, color: '#22c55e' },
    { id: 'support', name: t('campaignMgmt.types.support'), icon: HeadphonesIcon, color: '#3b82f6' },
    { id: 'collections', name: t('campaignMgmt.types.collections'), icon: Target, color: '#f59e0b' },
    { id: 'surveys', name: t('campaignMgmt.types.surveys'), icon: MessageSquare, color: '#8b5cf6' },
    { id: 'appointments', name: t('campaignMgmt.types.appointments'), icon: Calendar, color: '#06b6d4' },
    { id: 'retention', name: t('campaignMgmt.types.retention'), icon: Users, color: '#ef4444' }
  ];

  const industries = [
    { id: 'banking', name: t('campaignMgmt.industries.banking'), icon: Building },
    { id: 'healthcare', name: t('campaignMgmt.industries.healthcare'), icon: Activity },
    { id: 'retail', name: t('campaignMgmt.industries.retail'), icon: Star },
    { id: 'telecom', name: t('campaignMgmt.industries.telecom'), icon: Phone },
    { id: 'insurance', name: t('campaignMgmt.industries.insurance'), icon: AlertCircle },
    { id: 'automotive', name: t('campaignMgmt.industries.automotive'), icon: MapPin }
  ];

  // Filter campaigns
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesIndustry = selectedIndustry === 'all' || campaign.industry === selectedIndustry;
    const matchesType = selectedType === 'all' || campaign.type === selectedType;

    return matchesIndustry && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#22c55e';
      case 'paused': return '#f59e0b';
      case 'completed': return '#6b7280';
      case 'draft': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'voicebot': return Bot;
      case 'chatbot': return MessageSquare;
      case 'human': return Users;
      case 'hybrid': return Zap;
      default: return Phone;
    }
  };

  const getCampaignTypeData = (typeId) => {
    return campaignTypes.find(type => type.id === typeId) || campaignTypes[0];
  };

  const getIndustryData = (industryId) => {
    return industries.find(industry => industry.id === industryId) || industries[0];
  };

  const handleViewDetails = (campaign) => {
    setSelectedCampaignForDetails(campaign);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCampaignForDetails(null);
  };

  const handleConfigureCampaign = (campaign) => {
    setSelectedCampaignForConfig(campaign);
    setShowConfigModal(true);
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setSelectedCampaignForConfig(null);
  };

  const handleSaveCampaignConfig = async () => {
    const urlInput = document.getElementById('looker-url');
    const lookerUrl = (urlInput?.value || '').trim();

    // Nuevos campos por pestaña (opcionales)
    const urlGeneral = (document.getElementById('looker-url-general')?.value || '').trim();
    const urlTemporal = (document.getElementById('looker-url-temporal')?.value || '').trim();
    const urlDdi = (document.getElementById('looker-url-ddi')?.value || '').trim();
    const urlConversion = (document.getElementById('looker-url-conversion')?.value || '').trim();

    // Construir objeto por pestaña si alguno existe
    const perTab = {};
    if (urlGeneral) perTab['general-metrics'] = urlGeneral;
    if (urlTemporal) perTab['temporal-analysis'] = urlTemporal;
    if (urlDdi) perTab['ddi-campaigns'] = urlDdi;
    if (urlConversion) perTab['conversion-kpis'] = urlConversion;

    // Validaciones básicas si se rellenan
    const validateUrl = (u) => !u || u.includes('lookerstudio.google.com');
    if (![lookerUrl, urlGeneral, urlTemporal, urlDdi, urlConversion].every(validateUrl)) {
      alert('Todas las URLs deben ser de Looker Studio (lookerstudio.google.com)');
      return;
    }

    // Si no hay single ni per-tab, avisar
    if (!lookerUrl && Object.keys(perTab).length === 0) {
      alert('Introduce al menos una URL (única o por pestaña) de Looker Studio');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/campaigns/${selectedCampaignForConfig.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          // Si hay per-tab, guardamos JSON en el campo existente para no tocar el esquema
          looker_dashboard_url: Object.keys(perTab).length > 0 ? JSON.stringify(perTab) : lookerUrl
        })
      });

      if (response.ok) {
        // Actualizar el estado local de la campaña
        setCampaigns(prevCampaigns =>
          prevCampaigns.map(campaign =>
            campaign.id === selectedCampaignForConfig.id
              ? { ...campaign, looker_dashboard_url: lookerUrl }
              : campaign
          )
        );

        // Mostrar mensaje de éxito
        const successMsg = document.createElement('div');
        successMsg.textContent = `✅ Dashboard(s) de Looker Studio configurado(s) para "${selectedCampaignForConfig.name}"`;
        successMsg.style.cssText = `
          position: fixed; top: 20px; right: 20px; z-index: 10000;
          background: #22c55e; color: white; padding: 12px 20px;
          border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          font-weight: 500; font-size: 14px; max-width: 400px;
        `;
        document.body.appendChild(successMsg);
        setTimeout(() => {
          if (document.body.contains(successMsg)) {
            document.body.removeChild(successMsg);
          }
        }, 5000);

        handleCloseConfigModal();
      } else {
        throw new Error('Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error saving campaign config:', error);

      // Mostrar mensaje de error
      const errorMsg = document.createElement('div');
      errorMsg.textContent = `❌ Error al guardar la configuración. Inténtalo nuevamente.`;
      errorMsg.style.cssText = `
        position: fixed; top: 20px; right: 20px; z-index: 10000;
        background: #ef4444; color: white; padding: 12px 20px;
        border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        font-weight: 500; font-size: 14px; max-width: 400px;
      `;
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        if (document.body.contains(errorMsg)) {
          document.body.removeChild(errorMsg);
        }
      }, 5000);
    }
  };

  // Compute stats
  const activeCount = filteredCampaigns.filter(c => c.status === 'active').length;
  const totalContacts = filteredCampaigns.reduce((sum, c) => sum + c.contacts, 0);
  const avgSuccess = filteredCampaigns.length > 0 ? (filteredCampaigns.reduce((sum, c) => sum + c.success_rate, 0) / filteredCampaigns.length).toFixed(1) : 0;
  const totalRevenue = filteredCampaigns.reduce((sum, c) => sum + c.revenue, 0);

  if (loading) {
    return (
      <div className="campaign-management dashboard-container">
        <FloatingParticles count={20} />
        <div className="loading-enhanced">
          <div className="spinner-enhanced"></div>
          <p>Cargando campañas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-management dashboard-container">
        <FloatingParticles count={20} />
        <div className="message-enhanced error">
          <p>{error}</p>
          <EnhancedButton onClick={fetchCampaigns}>
            Reintentar
          </EnhancedButton>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-management dashboard-container" key={`campaigns-${currentLanguage}-${renderKey}`}>
      <FloatingParticles count={25} />
      
      <PageHeader
        title={t('campaigns.management.title')}
        subtitle={t('campaigns.management.subtitle')}
        icon={<Target />}
      />

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card metric-card-enhanced animate-delay-100">
          <div className="stat-card-header">
            <div className="stat-label">{t('campaignMgmt.statsActive')}</div>
            <div className="stat-icon active">
              <Activity size={18} />
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-number">{activeCount}</div>
            <div className="stat-trend">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
        </div>
        <div className="stat-card metric-card-enhanced animate-delay-200">
          <div className="stat-card-header">
            <div className="stat-label">{t('campaignMgmt.statsContacts')}</div>
            <div className="stat-icon contacts">
              <Users size={18} />
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-number">{totalContacts.toLocaleString()}</div>
            <div className="stat-trend">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
        </div>
        <div className="stat-card metric-card-enhanced animate-delay-300">
          <div className="stat-card-header">
            <div className="stat-label">{t('campaignMgmt.statsSuccess')}</div>
            <div className="stat-icon success">
              <TrendingUp size={18} />
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-number">{avgSuccess}%</div>
            <div className="stat-trend">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
        </div>
        <div className="stat-card metric-card-enhanced animate-delay-400">
          <div className="stat-card-header">
            <div className="stat-label">{t('campaignMgmt.statsRevenue')}</div>
            <div className="stat-icon revenue">
              <DollarSign size={18} />
            </div>
          </div>
          <div className="stat-content">
            <div className="stat-number">€{Math.floor(totalRevenue / 1000)}K</div>
            <div className="stat-trend">
              <TrendingUp size={12} />
              <span>+0%</span>
            </div>
          </div>
        </div>
      </div>



      {/* Campaign List */}
      <div className="campaign-list">
        {filteredCampaigns.length > 0 ? filteredCampaigns.map(campaign => {
          const typeData = getCampaignTypeData(campaign.type);
          const industryData = getIndustryData(campaign.industry);
          const ChannelIcon = getChannelIcon(campaign.channel);
          
          return (
            <div key={campaign.id} className="campaign-card">
              <div className="campaign-main">
                <div className="campaign-info">
                  <div className="campaign-header-row">
                    <div className="campaign-title-section">
                      <typeData.icon size={20} style={{ color: typeData.color }} />
                      <h3 className="campaign-name">{campaign.name}</h3>
                      <span className="campaign-industry">
                        <industryData.icon size={14} />
                        {industryData.name}
                      </span>
                    </div>
                  </div>

                  <div className="campaign-meta">
                    <div className="meta-item">
                      <div className="meta-label">Estado</div>
                      <div className="campaign-status" style={{ color: getStatusColor(campaign.status) }}>
                        {campaign.status === 'active' && 'Activa'}
                        {campaign.status === 'paused' && 'Pausada'}
                        {campaign.status === 'completed' && 'Completada'}
                        {campaign.status === 'draft' && 'Borrador'}
                      </div>
                    </div>
                    <div className="meta-item">
                      <div className="meta-label">Canal</div>
                      <div className="channel-info">
                        <ChannelIcon size={14} />
                        {campaign.agent}
                      </div>
                    </div>
                    <div className="meta-item">
                      <div className="meta-label">Creada</div>
                      <div className="meta-value">{campaign.created}</div>
                    </div>
                  </div>

                  <div className="campaign-progress">
                    <div className="progress-header">
                      <span>Progreso: 0 / 0</span>
                      <span>0%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: '0%', backgroundColor: typeData.color }}></div>
                    </div>
                  </div>
                </div>

                <div className="campaign-metrics">
                  <div className="metric-group">
                    <div className="metric-item">
                      <div className="metric-icon success">
                        <Target size={16} />
                      </div>
                      <div className="metric-content">
                        <div className="metric-value">0%</div>
                        <div className="metric-label">Éxito</div>
                      </div>
                    </div>
                    <div className="metric-item">
                      <div className="metric-icon contacts">
                        <Phone size={16} />
                      </div>
                      <div className="metric-content">
                        <div className="metric-value">0%</div>
                        <div className="metric-label">Contacto</div>
                      </div>
                    </div>
                    <div className="metric-item">
                      <div className="metric-icon duration">
                        <Clock size={16} />
                      </div>
                      <div className="metric-content">
                        <div className="metric-value">0s</div>
                        <div className="metric-label">Duración</div>
                      </div>
                    </div>
                    {campaign.revenue > 0 && (
                      <div className="metric-item">
                        <div className="metric-icon revenue">
                          <DollarSign size={16} />
                        </div>
                        <div className="metric-content">
                          <div className="metric-value">€0K</div>
                          <div className="metric-label">Ingresos</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="campaign-controls-section">
                    {campaign.status === 'active' && (
                      <button className="control-btn pause">
                        <Pause size={14} />
                        Pausar Campaña
                      </button>
                    )}
                    {campaign.status === 'paused' && (
                      <button className="control-btn play">
                        <Play size={14} />
                        Reanudar Campaña
                      </button>
                    )}
                    <button
                      className="control-btn details"
                      onClick={() => handleViewDetails(campaign)}
                    >
                      <BarChart3 size={14} />
                      Ver Detalles
                      <ChevronRight size={12} />
                    </button>
                    <button
                      className="control-btn config"
                      onClick={() => handleConfigureCampaign(campaign)}
                    >
                      <SettingsIcon size={14} />
                      Configurar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-10">
            No hay campañas disponibles.
          </div>
        )}
      </div>

      {/* Campaign Details Modal */}
      <CampaignDetailsModal
        campaign={selectedCampaignForDetails}
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
      />

      <style>{`
        .campaign-management {
          max-width: 100%;
          padding: 0;
          background: var(--neutral-bg-primary);
          backdrop-filter: var(--glass-backdrop);
          border-radius: 16px;
          border: 1.5px solid var(--neutral-border);
          box-shadow: 
            0 8px 32px var(--neutral-shadow),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .campaign-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
        }

        .campaign-title {
          font-size: 28px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 8px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .campaign-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: transparent;
          backdrop-filter: none;
          border-radius: 8px;
          padding: 6px 10px;
          border: 1px solid rgba(13, 44, 107, 0.35);
          display: flex;
          flex-direction: column;
          gap: 10px;
          box-shadow: 0 8px 20px rgba(13, 44, 107, 0.25);
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          position: relative;
          overflow: visible;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(13, 44, 107, 0.35);
        }

        .stat-icon {
          width: 26px;
          height: 26px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f46f42;
          background: rgba(244, 111, 66, 0.15);
          border: 1px solid #f46f42;
          flex-shrink: 0;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 2px 6px rgba(13, 44, 107, 0.35);
          position: absolute;
          top: 6px;
          right: 6px;
        }

        .stat-icon.active,
        .stat-icon.contacts,
        .stat-icon.success,
        .stat-icon.revenue { 
          background: rgba(244, 111, 66, 0.15);
          border: 1px solid #f46f42;
          color: #f46f42;
        }

        .stat-icon svg {
          color: #f46f42 !important;
          stroke: #f46f42 !important;
        }

        .stat-number {
          font-size: 1.4rem;
          font-weight: 700;
          color: #0c2c6c;
          line-height: 1;
          text-shadow: none;
          letter-spacing: -0.01em;
          margin-top: 0;
          margin-bottom: 0;
          display: block;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-label {
          font-size: 0.65rem;
          font-weight: 600;
          color: #0c2c6c;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          line-height: 1;
          margin: 0;
        }






        .campaign-list {
          display: flex;
          flex-direction: column;
        }

        .campaign-card {
          background: var(--neutral-bg-secondary);
          backdrop-filter: var(--glass-backdrop);
          border-radius: 12px;
          border: 1.5px solid var(--neutral-border-light);
          margin-bottom: 16px;
          transition: all 0.3s ease;
          box-shadow: 
            0 8px 32px var(--neutral-shadow),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .campaign-card:hover {
          border-color: var(--neutral-border);
          transform: translateY(-2px);
          box-shadow: 
            0 16px 48px var(--neutral-shadow),
            0 0 0 1px var(--neutral-border-light),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
        }

        .campaign-main {
          padding: 24px;
        }

        .campaign-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
        }

        .campaign-title-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .campaign-name {
          font-size: 18px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
          margin: 0;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }

        .campaign-industry {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          padding: 4px 8px;
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 6px;
        }

        .campaign-meta {
          display: flex;
          gap: 32px;
          margin-bottom: 20px;
        }

        .meta-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .meta-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          font-weight: 500;
        }

        .campaign-status {
          font-size: 14px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
        }

        .channel-info {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
        }

        .meta-value {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
        }

        .campaign-progress {
          margin-bottom: 24px;
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }

        .progress-bar {
          height: 8px;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 4px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .progress-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .campaign-metrics {
          margin-bottom: 24px;
        }

        .metric-group {
          display: flex;
          gap: 24px;
        }

        .metric-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .metric-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .metric-icon.success { background: var(--primary-color); }
        .metric-icon.contacts { background: var(--secondary-color); }
        .metric-icon.duration { background: var(--accent-color); }
        .metric-icon.revenue { background: var(--neutral-gradient-primary); }

        .metric-value {
          font-size: 16px;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.95);
          line-height: 1;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }

        .metric-label {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          margin-top: 2px;
        }

        .campaign-controls-section {
          display: flex;
          gap: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          margin-top: 20px;
          padding-top: 20px;
        }

        .control-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 1.5px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(10px);
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          color: rgba(255, 255, 255, 0.8);
        }

        .control-btn.pause {
          color: #f59e0b;
          border-color: #f59e0b;
        }

        .control-btn.pause:hover {
          background: rgba(245, 158, 11, 0.2);
          border-color: #f59e0b;
          transform: translateY(-1px);
        }

        .control-btn.play {
          color: #22c55e;
          border-color: #22c55e;
        }

        .control-btn.play:hover {
          background: rgba(34, 197, 94, 0.2);
          border-color: #22c55e;
          transform: translateY(-1px);
        }

        .control-btn.details {
          color: #3b82f6;
          border-color: #3b82f6;
        }

        .control-btn.details:hover {
          background: rgba(59, 130, 246, 0.2);
          border-color: #3b82f6;
          transform: translateY(-1px);
        }

        .control-btn.config {
          color: #8b5cf6;
          border-color: #8b5cf6;
        }

        .control-btn.config:hover {
          background: rgba(139, 92, 246, 0.2);
          border-color: #8b5cf6;
          transform: translateY(-1px);
        }

        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }


          .campaign-meta {
            flex-direction: column;
            gap: 12px;
          }

          .metric-group {
            flex-wrap: wrap;
            gap: 16px;
          }
        }

        @media (max-width: 640px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .campaign-header {
            flex-direction: column;
            gap: 16px;
          }
        }

        /* Configuration Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          padding: 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-body {
          padding: 24px;
        }

        .config-section {
          margin-bottom: 24px;
        }

        .config-section h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .config-section p {
          color: #64748b;
          margin-bottom: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-group input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus {
          outline: none;
          border-color: #7c3aed;
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .form-group small {
          display: block;
          color: #6b7280;
          font-size: 12px;
          margin-top: 4px;
        }

        .campaign-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .campaign-info-grid div {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .campaign-info-grid label {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
          margin: 0;
        }

        .campaign-info-grid span {
          font-size: 14px;
          color: #374151;
          font-weight: 500;
        }

        .modal-footer {
          padding: 24px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .btn-primary {
          background: #7c3aed;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .btn-primary:hover {
          background: #6d28d9;
        }

        .btn-secondary {
          background: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .close-btn {
          padding: 8px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #e5e7eb;
          color: #374151;
        }
      `}</style>

      {/* Campaign Configuration Modal */}
      {showConfigModal && selectedCampaignForConfig && (
        <div className="modal-overlay" onClick={handleCloseConfigModal}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Configurar Campaña: {selectedCampaignForConfig.name}</h2>
              <button className="close-btn" onClick={handleCloseConfigModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="config-section">
                <h3>Dashboard de Looker Studio</h3>
                <p>Configura la URL específica del dashboard que se mostrará para esta campaña.</p>

                <div className="form-group">
                  <label htmlFor="looker-url">URL del Dashboard</label>
                  <input
                    type="text"
                    id="looker-url"
                    placeholder="https://lookerstudio.google.com/embed/reporting/..."
                    defaultValue={selectedCampaignForConfig.looker_dashboard_url || ''}
                  />
                  <small>Ingresa la URL completa del dashboard de Looker Studio que deseas asociar a esta campaña.</small>
                </div>

              <div className="form-group">
                <label>URLs por pestaña (opcional)</label>
                <input
                  type="text"
                  id="looker-url-general"
                  placeholder="Mètriques Generals - https://lookerstudio.google.com/embed/reporting/..."
                  defaultValue=""
                  style={{ marginBottom: '8px' }}
                />
                <input
                  type="text"
                  id="looker-url-temporal"
                  placeholder="Anàlisi Temporal - https://lookerstudio.google.com/embed/reporting/..."
                  defaultValue=""
                  style={{ marginBottom: '8px' }}
                />
                <input
                  type="text"
                  id="looker-url-ddi"
                  placeholder="Campanyes DDI - https://lookerstudio.google.com/embed/reporting/..."
                  defaultValue=""
                  style={{ marginBottom: '8px' }}
                />
                <input
                  type="text"
                  id="looker-url-conversion"
                  placeholder="Conversió i KPIs - https://lookerstudio.google.com/embed/reporting/..."
                  defaultValue=""
                />
                <small>Si completas estas URLs, se usará una por pestaña en el informe detallado de esta campaña.</small>
              </div>
              </div>

              <div className="config-section">
                <h3>Información de la Campaña</h3>
                <div className="campaign-info-grid">
                  <div>
                    <label>Nombre:</label>
                    <span>{selectedCampaignForConfig.name}</span>
                  </div>
                  <div>
                    <label>Tipo:</label>
                    <span>{selectedCampaignForConfig.type}</span>
                  </div>
                  <div>
                    <label>Estado:</label>
                    <span>{selectedCampaignForConfig.status}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCloseConfigModal}
              >
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  handleSaveCampaignConfig();
                }}
              >
                Guardar Configuración
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;